import { useState, useCallback } from 'react';
import { SK_ESSENTIALS_MODE, SK_ESSENTIALS_TOOLS, SK_ENABLED_CHECKIN_QUESTIONS, SK_ACCESSIBILITY } from '../constants/storageKeys';
import type { AccessibilityPrefs } from '../NotificationPanel';

const ESSENTIALS_WIDGET_PRESET = ['checkin', 'medications', 'quick-access'] as const;
const ESSENTIALS_CHECKIN_PRESET = new Set([1, 5]); // waters check + pain only
const DEFAULT_WIDGET_ORDER = ['checkin', 'weather', 'journeys', 'medications', 'quick-access', 'patterns', 'goals', 'articles', 'circle', 'settings'];

interface EssentialsCallbacks {
  setWidgetVisible: (fn: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  saveWidgetLayout: (order: string[], visible: Record<string, boolean>) => void;
  widgetOrder: string[];
  setEnabledCheckinQuestions: (set: Set<number>) => void;
  accessibilityPrefs: AccessibilityPrefs;
  handleAccessibilityChange: (prefs: AccessibilityPrefs) => void;
}

export function useEssentials(callbacks: EssentialsCallbacks) {
  const [essentialsMode, setEssentialsModeValue] = useState(
    () => localStorage.getItem(SK_ESSENTIALS_MODE) === 'true'
  );
  const [essentialsTools, setEssentialsToolsValue] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(SK_ESSENTIALS_TOOLS);
      if (stored) return JSON.parse(stored);
    } catch { /* localStorage may be unavailable */ }
    return [...ESSENTIALS_WIDGET_PRESET];
  });

  const toggleEssentialsMode = useCallback((enabled?: boolean) => {
    const next = enabled ?? !essentialsMode;
    setEssentialsModeValue(next);
    localStorage.setItem(SK_ESSENTIALS_MODE, String(next));

    if (next) {
      const toolsToShow = [...new Set(['checkin', ...essentialsTools])];
      const nextVisible: Record<string, boolean> = {};
      DEFAULT_WIDGET_ORDER.forEach(id => { nextVisible[id] = toolsToShow.includes(id); });
      callbacks.setWidgetVisible(() => nextVisible);
      callbacks.saveWidgetLayout(callbacks.widgetOrder, nextVisible);

      callbacks.setEnabledCheckinQuestions(new Set(ESSENTIALS_CHECKIN_PRESET));
      localStorage.setItem(SK_ENABLED_CHECKIN_QUESTIONS, JSON.stringify([...ESSENTIALS_CHECKIN_PRESET]));

      if (!callbacks.accessibilityPrefs.reducedMotion) {
        const nextPrefs = { ...callbacks.accessibilityPrefs, reducedMotion: true };
        callbacks.handleAccessibilityChange(nextPrefs);
        try { localStorage.setItem(SK_ACCESSIBILITY, JSON.stringify(nextPrefs)); } catch { /* localStorage may be unavailable */ }
        document.documentElement.classList.add('reduced-motion');
      }
    } else {
      const nextVisible: Record<string, boolean> = {};
      DEFAULT_WIDGET_ORDER.forEach(id => { nextVisible[id] = true; });
      callbacks.setWidgetVisible(() => nextVisible);
      callbacks.saveWidgetLayout(callbacks.widgetOrder, nextVisible);

      const allQuestions = new Set(Array.from({ length: 10 }, (_, i) => i + 1));
      callbacks.setEnabledCheckinQuestions(allQuestions);
      localStorage.setItem(SK_ENABLED_CHECKIN_QUESTIONS, JSON.stringify([...allQuestions]));
    }
  }, [essentialsMode, essentialsTools, callbacks]);

  const updateEssentialsTools = useCallback((tools: string[]) => {
    setEssentialsToolsValue(tools);
    localStorage.setItem(SK_ESSENTIALS_TOOLS, JSON.stringify(tools));
    if (essentialsMode) {
      const toolsToShow = [...new Set(['checkin', ...tools])];
      const nextVisible: Record<string, boolean> = {};
      DEFAULT_WIDGET_ORDER.forEach(id => { nextVisible[id] = toolsToShow.includes(id); });
      callbacks.setWidgetVisible(() => nextVisible);
      callbacks.saveWidgetLayout(callbacks.widgetOrder, nextVisible);
    }
  }, [essentialsMode, callbacks]);

  return {
    essentialsMode,
    essentialsTools,
    toggleEssentialsMode,
    updateEssentialsTools,
  };
}
